/**
 * Page Rapprochement > Wave > Liste des paiements (/admin/rapprochement/wave/paiements)
 * ------------------------------------------------------------
 * 3 sub-tabs :
 * - Toutes les demandes (workflow)
 * - Par opération (matrice)
 * - Transactions Wave non affectées
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Send, RefreshCw, Search, Filter, Loader2, CheckCircle2, X, Copy,
  Eye, XCircle, AlertCircle, Wallet, Link2, Ban, ArrowRight, Upload,
  ClipboardCheck, ShieldAlert,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { RequirePermission } from '../../components/permissions/RequirePermission';
import { PERMS } from '../../utils/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fxof = (v) => v == null ? '—' : `${Math.round(v).toLocaleString('fr-FR')} XOF`;
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(v); }
};

const STATUS_META = {
  draft: { label: 'Brouillon', color: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  validated: { label: 'Validée', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  paid_declared: { label: 'Payée déclarée', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  reconciled: { label: 'Rapprochée', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Annulée', color: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

const copyToClipboard = (text, label) => {
  try {
    navigator.clipboard.writeText(text);
    toast({ title: `✓ ${label} copié`, description: text.slice(0, 60) });
  } catch {
    toast({ title: 'Erreur copie', variant: 'destructive' });
  }
};

// ═══════════════════════════════════════════════════════════════════
// Cancel dialog
// ═══════════════════════════════════════════════════════════════════
const CancelDialog = ({ open, onClose, pr, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason || reason.trim().length < 3) {
      toast({ title: 'Motif obligatoire (min 3 caractères)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/payment-requests/${pr.id}/cancel`, { reason });
      toast({ title: 'Demande annulée' });
      onSuccess?.(); onClose(); setReason('');
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };
  if (!pr) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pr-cancel-dialog">
        <DialogHeader>
          <DialogTitle>Annuler {pr.refPaiement}</DialogTitle>
          <DialogDescription>Motif obligatoire (min. 3 caractères) pour la traçabilité audit.</DialogDescription>
        </DialogHeader>
        <Label className="text-xs font-semibold">Motif</Label>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  data-testid="pr-cancel-reason" placeholder="Raison de l'annulation…" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="pr-cancel-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
            Annuler la demande
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Declare paid dialog
// ═══════════════════════════════════════════════════════════════════
const DeclarePaidDialog = ({ open, onClose, pr, onSuccess }) => {
  const [waveRef, setWaveRef] = useState('');
  const [note, setNote] = useState('');
  const [proof, setProof] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setWaveRef(''); setNote(''); setProof(null); }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/payment-requests/${pr.id}/declare-paid`, {
        waveTransactionRef: waveRef || null,
        declarationNote: note || null,
      });
      if (proof) {
        const fd = new FormData();
        fd.append('file', proof);
        await axios.post(`${API}/payment-requests/${pr.id}/proof`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast({ title: `${pr.refPaiement} marquée payée` });
      onSuccess?.(); onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };
  if (!pr) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pr-declare-paid-dialog">
        <DialogHeader><DialogTitle>Marquer payé — {pr.refPaiement}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-500 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠ Cette déclaration n&apos;est PAS la preuve comptable finale. La preuve définitive reste la transaction retrouvée dans l&apos;export Wave importé.
          </div>
          <div>
            <Label className="text-xs">Réf transaction Wave (si visible)</Label>
            <Input value={waveRef} onChange={(e) => setWaveRef(e.target.value)} placeholder="ex : pt-XXXXXX" data-testid="pr-paid-waveref" />
          </div>
          <div>
            <Label className="text-xs">Note de déclaration</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="pr-paid-note" />
          </div>
          <div>
            <Label className="text-xs">Preuve (capture Wave, jpg/png/pdf, ≤5MB)</Label>
            <Input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} data-testid="pr-paid-proof" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pr-paid-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Marquer payé
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Create PaymentRequest dialog
// ═══════════════════════════════════════════════════════════════════
export const CreatePaymentRequestDialog = ({ open, onClose, operationId, onSuccess }) => {
  const [type, setType] = useState('avance');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewInfo, setPreviewInfo] = useState(null);

  useEffect(() => {
    if (!open || !operationId) return;
    axios.get(`${API}/transport-requests/${operationId}`).then((r) => setPreviewInfo(r.data)).catch(() => {});
  }, [open, operationId]);

  const submit = async () => {
    const amt = parseInt(amount);
    if (!amt || amt <= 0) {
      toast({ title: 'Montant invalide', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/payment-requests`, {
        operationId, type, amount: amt, notes: notes || undefined,
      });
      const w = r.data.warnings || [];
      toast({
        title: `${r.data.paymentRequest.refPaiement} créée`,
        description: w.length ? `⚠ ${w.length} alerte(s) : ${w[0]}` : 'Prête pour validation finance',
      });
      onSuccess?.(r.data.paymentRequest); onClose();
      setAmount(''); setNotes(''); setType('avance');
    } catch (e) {
      toast({ title: 'Erreur création', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pr-create-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle demande de paiement Wave</DialogTitle>
          <DialogDescription>Crée une référence PAY-YYYYMM-NNNNN et un commentaire Wave à copier.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {previewInfo && (
            <div className="p-2 bg-zinc-50 border border-zinc-200 rounded text-xs space-y-1">
              <div><span className="text-zinc-500">Opération :</span> <strong>{previewInfo.operationNumber}</strong></div>
              <div><span className="text-zinc-500">Chauffeur :</span> {previewInfo.assignedDriver?.name || 'N/A'}</div>
              <div><span className="text-zinc-500">Num Wave :</span> {previewInfo.assignedDriver?.waveNumber || previewInfo.assignedDriver?.phone || 'N/A'}</div>
              <div><span className="text-zinc-500">Tarif transporteur :</span> {fxof(previewInfo.carrierPrice)}</div>
            </div>
          )}
          <div>
            <Label className="text-xs">Type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="pr-create-type"
                    className="w-full border border-zinc-200 rounded px-2 py-1.5 text-sm">
              <option value="avance">Avance</option>
              <option value="solde">Solde</option>
              <option value="total">Total</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Montant (XOF)</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pr-create-amount" placeholder="ex: 150000" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pr-create-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#16A34A] hover:bg-emerald-700" data-testid="pr-create-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Créer la demande
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Non-concernée Wave dialog
// ═══════════════════════════════════════════════════════════════════
export const NoWaveDialog = ({ open, onClose, operationId, onSuccess }) => {
  const [reason, setReason] = useState('cash');
  const [reasonText, setReasonText] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (reason === 'other' && reasonText.trim().length < 3) {
      toast({ title: 'Précisez le motif', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/operations/${operationId}/no-wave`, {
        reason, reasonText: reason === 'other' ? reasonText : null,
      });
      toast({ title: 'Opération marquée non concernée Wave' });
      onSuccess?.(); onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="op-no-wave-dialog">
        <DialogHeader>
          <DialogTitle>Marquer non concernée Wave</DialogTitle>
          <DialogDescription>Précisez pourquoi cette opération ne passera pas par Wave.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <Label className="text-xs">Motif</Label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="op-no-wave-reason"
                  className="w-full border border-zinc-200 rounded px-2 py-1.5">
            <option value="cash">Payée en espèces</option>
            <option value="bank_transfer">Payée par virement</option>
            <option value="other_mobile_money">Autre mobile money (Orange, Moov…)</option>
            <option value="direct_client">Paiement direct client/transporteur</option>
            <option value="compensation">Compensation interne</option>
            <option value="cancelled_op">Opération annulée</option>
            <option value="not_yet_paid">Non encore payée</option>
            <option value="litigation">En litige</option>
            <option value="other">Autre (préciser)</option>
          </select>
          {reason === 'other' && (
            <Textarea rows={2} value={reasonText} onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Précisez le motif…" data-testid="op-no-wave-reason-text" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button onClick={submit} disabled={saving} data-testid="op-no-wave-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PaymentRequest row (utilisé dans les 2 vues)
// ═══════════════════════════════════════════════════════════════════
const PRRow = ({ pr, onValidate, onDeclarePaid, onCancel }) => {
  const meta = STATUS_META[pr.status] || { label: pr.status, color: 'bg-zinc-100' };
  return (
    <tr className="hover:bg-zinc-50" data-testid={`pr-row-${pr.refPaiement}`}>
      <td className="p-2 font-mono text-[11px] whitespace-nowrap">{pr.refPaiement}</td>
      <td className="p-2 text-xs">{pr.operationNumber || '—'}</td>
      <td className="p-2 text-xs">{pr.driverName || '—'}</td>
      <td className="p-2 text-xs font-mono">{pr.waveNumber}</td>
      <td className="p-2"><Badge variant="outline" className="text-[10px]">{pr.type}</Badge></td>
      <td className="p-2 text-right tabular-nums font-semibold">{Math.round(pr.amount).toLocaleString('fr-FR')}</td>
      <td className="p-2"><span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${meta.color}`}>{meta.label}</span></td>
      <td className="p-2 whitespace-nowrap text-right space-x-1">
        <button onClick={() => copyToClipboard(pr.waveNumber, 'Numéro Wave')} className="text-zinc-500 hover:text-zinc-800" title="Copier numéro" data-testid={`pr-copy-num-${pr.refPaiement}`}>
          <Copy className="h-3 w-3 inline" />
        </button>
        <button onClick={() => copyToClipboard(String(pr.amount), 'Montant')} className="text-zinc-500 hover:text-zinc-800" title="Copier montant" data-testid={`pr-copy-amt-${pr.refPaiement}`}>
          <Wallet className="h-3 w-3 inline" />
        </button>
        <button onClick={() => copyToClipboard(pr.waveComment, 'Commentaire Wave')} className="text-zinc-500 hover:text-zinc-800" title="Copier commentaire" data-testid={`pr-copy-comment-${pr.refPaiement}`}>
          <ClipboardCheck className="h-3 w-3 inline" />
        </button>
        {pr.status === 'draft' && (
          <button onClick={() => onValidate(pr)} className="text-blue-600 hover:text-blue-800 text-[11px] font-semibold" data-testid={`pr-validate-${pr.refPaiement}`}>Valider</button>
        )}
        {(pr.status === 'draft' || pr.status === 'validated') && (
          <button onClick={() => onDeclarePaid(pr)} className="text-emerald-600 hover:text-emerald-800 text-[11px] font-semibold" data-testid={`pr-pay-${pr.refPaiement}`}>Payer</button>
        )}
        {pr.status !== 'reconciled' && pr.status !== 'cancelled' && (
          <button onClick={() => onCancel(pr)} className="text-rose-500 hover:text-rose-800 text-[11px]" data-testid={`pr-cancel-${pr.refPaiement}`}><X className="h-3 w-3 inline" /></button>
        )}
      </td>
    </tr>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════
const WavePaymentsPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [prs, setPrs] = useState([]);
  const [totalPRs, setTotalPRs] = useState(0);
  const [unmatchedTxs, setUnmatchedTxs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [cancelDlg, setCancelDlg] = useState({ open: false, pr: null });
  const [paidDlg, setPaidDlg] = useState({ open: false, pr: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prsRes, txRes] = await Promise.all([
        axios.get(`${API}/payment-requests`, {
          params: { status: statusFilter || undefined, q: query || undefined, limit: 200 },
        }),
        axios.get(`${API}/wave/transactions`, {
          params: { is_outflow: true, match_status: 'non_rapprochee', limit: 100 },
        }).catch(() => ({ data: { items: [] } })),
      ]);
      setPrs(prsRes.data.items || []);
      setTotalPRs(prsRes.data.total || 0);
      setUnmatchedTxs(txRes.data.items || []);
    } catch (e) {
      toast({ title: 'Erreur chargement', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [statusFilter, query]);

  useEffect(() => { load(); }, [load]);

  const validatePR = async (pr) => {
    if (!window.confirm(`Valider ${pr.refPaiement} ?`)) return;
    try {
      await axios.post(`${API}/payment-requests/${pr.id}/validate`, { forceOverride: false });
      toast({ title: 'Validée' });
      load();
    } catch (e) {
      // Si 4-yeux, proposer forceOverride
      if (e?.response?.data?.detail?.includes('4-yeux')) {
        if (window.confirm('Vous êtes le créateur. Forcer la validation (bypass 4-yeux) ?')) {
          try {
            await axios.post(`${API}/payment-requests/${pr.id}/validate`, { forceOverride: true });
            toast({ title: 'Validée (bypass)' });
            load();
          } catch (e2) {
            toast({ title: 'Erreur', description: e2?.response?.data?.detail, variant: 'destructive' });
          }
        }
      } else {
        toast({ title: 'Erreur', description: e?.response?.data?.detail, variant: 'destructive' });
      }
    }
  };

  // Regroupement par opération pour le tab "Par opération"
  const groupedByOp = useMemo(() => {
    const groups = {};
    prs.forEach(pr => {
      const key = pr.operationId || 'unknown';
      if (!groups[key]) groups[key] = { operationNumber: pr.operationNumber, clientName: pr.clientName, prs: [] };
      groups[key].prs.push(pr);
    });
    return Object.entries(groups);
  }, [prs]);

  return (
    <RequirePermission permission={PERMS.PAYMENT_REQUESTS_VIEW}>
      <div className="p-4 space-y-4" data-testid="wave-payments-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center">
              <Send className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <div className="text-lg font-bold text-zinc-900">Liste des paiements Wave</div>
              <div className="text-xs text-zinc-500">Demandes de paiement, workflow et rapprochement</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="wp-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 p-2 bg-white border border-zinc-200 rounded-lg">
          <Filter className="h-3 w-3 text-zinc-400" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-xs border border-zinc-200 rounded px-2 py-1" data-testid="wp-filter-status">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="PAY-... · op · chauffeur · num Wave…" className="pl-7 h-8 text-xs" data-testid="wp-filter-query" />
          </div>
          <span className="text-xs text-zinc-500">{totalPRs} demandes</span>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-100 h-9" data-testid="wp-tabs">
            <TabsTrigger value="all" className="text-xs data-[state=active]:bg-white" data-testid="wp-tab-all">
              Toutes les demandes
            </TabsTrigger>
            <TabsTrigger value="by-op" className="text-xs data-[state=active]:bg-white" data-testid="wp-tab-by-op">
              Par opération
            </TabsTrigger>
            <TabsTrigger value="unmatched" className="text-xs data-[state=active]:bg-white" data-testid="wp-tab-unmatched">
              Non affectées ({unmatchedTxs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-3">
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs" data-testid="wp-pr-table">
                <thead className="bg-zinc-50 uppercase text-[10px] text-zinc-600">
                  <tr>
                    <th className="p-2 text-left">Ref PAY</th>
                    <th className="p-2 text-left">Opération</th>
                    <th className="p-2 text-left">Chauffeur</th>
                    <th className="p-2 text-left">Num Wave</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-right">Montant</th>
                    <th className="p-2 text-left">Statut</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {loading && <tr><td colSpan={8} className="text-center py-6 text-zinc-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Chargement…</td></tr>}
                  {!loading && prs.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-zinc-400">Aucune demande</td></tr>}
                  {prs.map(pr => (
                    <PRRow key={pr.id} pr={pr}
                           onValidate={validatePR}
                           onDeclarePaid={(p) => setPaidDlg({ open: true, pr: p })}
                           onCancel={(p) => setCancelDlg({ open: true, pr: p })} />
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="by-op" className="mt-3">
            <div className="space-y-3">
              {groupedByOp.length === 0 && <div className="text-center py-8 text-zinc-400 bg-white border border-zinc-200 rounded-xl">Aucune opération</div>}
              {groupedByOp.map(([opId, g]) => (
                <div key={opId} className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid={`wp-group-${opId}`}>
                  <div className="p-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                    <div>
                      <span className="font-mono text-xs font-bold">{g.operationNumber || opId}</span>
                      <span className="text-zinc-500 ml-2 text-xs">{g.clientName || '—'}</span>
                    </div>
                    <span className="text-xs text-zinc-500">{g.prs.length} demande(s)</span>
                  </div>
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-zinc-100">
                      {g.prs.map(pr => (
                        <PRRow key={pr.id} pr={pr}
                               onValidate={validatePR}
                               onDeclarePaid={(p) => setPaidDlg({ open: true, pr: p })}
                               onCancel={(p) => setCancelDlg({ open: true, pr: p })} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="unmatched" className="mt-3">
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="p-3 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {unmatchedTxs.length} transaction(s) Wave sortie(s) non rapprochée(s). Actions : affecter à opération, hors périmètre, ignorer.
              </div>
              <table className="w-full text-xs" data-testid="wp-unmatched-table">
                <thead className="bg-zinc-50 uppercase text-[10px] text-zinc-600">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Réf Wave</th>
                    <th className="p-2 text-left">Bénéficiaire</th>
                    <th className="p-2 text-right">Envoyé</th>
                    <th className="p-2 text-right">Frais</th>
                    <th className="p-2 text-left">Commentaire</th>
                    <th className="p-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {unmatchedTxs.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-zinc-400">Aucune transaction non affectée</td></tr>}
                  {unmatchedTxs.map(tx => (
                    <tr key={tx.id} className="hover:bg-zinc-50" data-testid={`wp-unmatched-${tx.waveRef}`}>
                      <td className="p-2 whitespace-nowrap">{fmtDate(tx.date)}</td>
                      <td className="p-2 font-mono text-[10px]">{tx.waveRef}</td>
                      <td className="p-2">{tx.beneficiaryName || '—'}<div className="text-[10px] text-zinc-500">{tx.beneficiaryPhone}</div></td>
                      <td className="p-2 text-right tabular-nums font-medium">{tx.amountSent?.toLocaleString('fr-FR')}</td>
                      <td className="p-2 text-right tabular-nums text-amber-700">{tx.waveFee?.toLocaleString('fr-FR')}</td>
                      <td className="p-2 max-w-[220px] truncate" title={tx.comment}>{tx.comment || '—'}</td>
                      <td className="p-2 text-right">
                        <a href="/admin/finance/cockpit?tab=wave" className="text-emerald-600 hover:text-emerald-800 text-[11px] font-semibold inline-flex items-center gap-1">
                          Cockpit <ArrowRight className="h-3 w-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        <CancelDialog open={cancelDlg.open} pr={cancelDlg.pr}
                      onClose={() => setCancelDlg({ open: false, pr: null })} onSuccess={load} />
        <DeclarePaidDialog open={paidDlg.open} pr={paidDlg.pr}
                           onClose={() => setPaidDlg({ open: false, pr: null })} onSuccess={load} />
      </div>
    </RequirePermission>
  );
};

export default WavePaymentsPage;
